import { ACCOUNT_URL, EnergyPulse, WithHalfdot, Wordmark } from "@elyxeer/ui";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import { OfferingsSection } from "~/components/membership/OfferingsSection";
import { Screen } from "~/components/ui/Screen";
import { openExternal } from "~/lib/external-link";
import { useAuthStore } from "~/store/auth-store";

/**
 * Membership — the member's subscription status, or the shop that starts one.
 *
 * An active member sees their status and a link to the storefront account (that is
 * where Appstle's portal manages deliveries and cancellation). The only other account
 * that can reach this tab — an app-only gift recipient — sees the live offerings. A
 * regular account without confirmed access never gets here: the router redirects both
 * "none" and "unknown" to the standalone paywall.
 */
export function MembershipRoute() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);

  const isActive = user?.membership_status === "active";

  const header = (
    <Box>
      <Wordmark size={20} />
      {/* Roomier than the overline it replaced: the lockup's `30.5` hangs below
          its own baseline, so 8px left it sitting on the heading. */}
      <Typography variant="H1" sx={{ color: "text.primary", marginTop: 1.5 }}>
        <WithHalfdot size={11}>{t("membership.title")}</WithHalfdot>
      </Typography>
    </Box>
  );

  if (isActive) {
    return (
      <Screen maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3, md: 4 },
          }}
        >
          {header}
          <Paper
            variant="outlined"
            data-testid="membership.status"
            sx={{
              position: "relative",
              overflow: "hidden",
              padding: { xs: 3, md: 5 },
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <EnergyPulse
              size={280}
              opacity={0.22}
              blur={10}
              sx={{ position: "absolute", top: -110, right: -90, zIndex: 0 }}
            />
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                maxWidth: 520,
              }}
            >
              <Typography variant="H3" sx={{ color: "text.primary" }}>
                {t("membership.status.activeTitle")}
              </Typography>
              <Typography variant="T1" sx={{ color: "text.secondary" }}>
                {t("membership.status.activeBody")}
              </Typography>
              <Button
                variant="outlined"
                size="large"
                endIcon={<OpenInNewIcon sx={{ fontSize: "1rem" }} />}
                onClick={() => void openExternal(ACCOUNT_URL)}
                data-testid="membership.manage"
                sx={{ alignSelf: "flex-start" }}
              >
                {t("membership.status.manage")}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Screen>
    );
  }

  return (
    <Screen maxWidth="lg">
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, md: 4 } }}
      >
        {header}
        <OfferingsSection />
      </Box>
    </Screen>
  );
}

/**
 * Keep the retired membership URL pointed at Profile for ordinary members, while
 * giving an app-only gift recipient a checkout-capable physical-subscription upsell.
 */
export function MembershipUpsellRoute() {
  const user = useAuthStore((state) => state.user);
  const hasGiftedAppAccess =
    user?.has_membership_override && user.membership_status !== "active";

  return hasGiftedAppAccess ? (
    <MembershipRoute />
  ) : (
    <Navigate to="/profile" replace />
  );
}
